import type { Template } from "@/types/template";
import type { Evaluation } from "@/types/evaluation";
import { Link, useLocation } from "react-router-dom";
import { useEvaluationStore } from "@/store/useEvaluationStore";
import { useTemplateStore } from "@/store/useTemplateStore";
import { parseTemplate } from "@/parser/templateParser";

interface Segment {
  label: string;
  to?: string;
}

function buildSegments(
  parts: string[],
  templates: Template[],
  evaluations: Evaluation[],
): Segment[] {
  const segments: Segment[] = [];

  switch (parts[0]) {
    case "templates": {
      segments.push({ label: "Templates" });
      break;
    }
    case "build": {
      segments.push({ label: "Templates", to: "/templates" });
      if (parts[1]) {
        const template = templates.find((t) => t.id === parts[1]);
        if (template) {
          const parsed = parseTemplate(template.source);
          segments.push({ label: parsed.content.name || "Untitled" });
        }
      }
      break;
    }
    case "fill":
    case "unified": {
      segments.push({ label: "Evaluations", to: "/fill" });
      if (parts[1]) {
        const evaluation = evaluations.find((e) => e.id === parts[1]);
        if (evaluation) {
          segments.push({ label: evaluation.name || "Untitled" });
        }
      }
      break;
    }
    // No default
  }

  return segments;
}

export function Breadcrumbs() {
  const location = useLocation();
  const templates = useTemplateStore((s) => s.templates);
  const evaluations = useEvaluationStore((s) => s.evaluations);

  const parts = location.pathname.split("/").filter(Boolean);
  const segments = buildSegments(parts, templates, evaluations);

  if (segments.length === 0) {
    return null;
  }

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <span className="breadcrumb-sep">/</span>
      {segments.map((seg, i) => {
        const isLast = i === segments.length - 1;
        return (
          <span key={i} className="breadcrumb-item">
            {!isLast && seg.to ? (
              <Link to={seg.to} className="breadcrumb-link">
                {seg.label}
              </Link>
            ) : (
              <span className="breadcrumb-current">{seg.label}</span>
            )}
            {!isLast && <span className="breadcrumb-sep">/</span>}
          </span>
        );
      })}
    </nav>
  );
}
